import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { GraduationCap, Clock, Users, Award, ExternalLink, CheckCircle, BookOpen, Target, ChevronDown, ChevronUp, FileText, Percent } from 'lucide-react';

const IfcCsc = () => {
  const [showIfcSyllabus, setShowIfcSyllabus] = useState(false);

  const ifcChapters = [
    'Chapter 1 - The Role of the Mutual Fund Sales Representative',
    'Chapter 2 - Overview of the Canadian Financial Marketplace',
    'Chapter 3 - Overview of Economics',
    'Chapter 4 - Getting to Know the Client',
    'Chapter 5 - Behavioural Finance',
    'Chapter 6 - Tax and Retirement Planning',
    'Chapter 7 - Types of Investment Products and How They Are Traded',
    'Chapter 8 - Constructing Investment Portfolios',
    'Chapter 9 - Understanding Financial Statements',
    'Chapter 10 - The Modern Mutual Fund',
    'Chapter 11 - Conservative Mutual Fund Products',
    'Chapter 12 - Riskier Mutual Fund Products',
    'Chapter 13 - Alternative Managed Products',
    'Chapter 14 - Understanding Mutual Fund Performance',
    'Chapter 15 - Selecting a Mutual Fund',
    'Chapter 16 - Mutual Fund Fees and Services',
    'Chapter 17 - Mutual Fund Dealer Regulation',
    'Chapter 18 - Applying Ethical Standards to What You Have Learned',
  ];

  const ifcExamWeighting = [
    { topic: 'Introduction to the Mutual Funds Marketplace', weight: '13%' },
    { topic: 'The Know Your Client Communication Process', weight: '19%' },
    { topic: 'Understanding Investment Products and Portfolios', weight: '18%' },
    { topic: 'The Modern Mutual Fund', weight: '5%' },
    { topic: 'Analysis of Mutual Funds', weight: '10%' },
    { topic: 'Understanding Alternative Managed Products', weight: '3%' },
    { topic: 'Evaluating and Selecting Mutual Funds', weight: '16%' },
    { topic: 'Ethics, Compliance, and Mutual Fund Regulation', weight: '16%' },
  ];

  const ifcExamStructure = [
    { label: 'Number of Exams', value: '1' },
    { label: 'Exam Format', value: 'Proctored (remote or in-person)' },
    { label: 'Exam Duration', value: '3 hours' },
    { label: 'Question Format', value: 'Multiple Choice' },
    { label: 'Questions Per Exam', value: '100' },
    { label: 'Attempts Allowed', value: '3' },
    { label: 'Passing Grade', value: '60%' },
    { label: 'Hours of Study', value: '90 – 140 Hours' },
    { label: 'Enrolment Period', value: '1 Year' },
  ];

  const ifcSkills = [
    'Understand the Canadian financial markets and mutual funds industry',
    'Analyze the risk-return relationship of investments',
    'Assess mutual fund performance and fee structure',
    'Create and effectively manage client portfolios',
    'Apply the "Know Your Client" rule and client focused reforms',
    'Meet regulatory requirements for Mutual Fund Dealer License',
  ];

  const ceCredits = [
    { accreditation: 'IFSE SFPA', credits: '15' },
    { accreditation: 'IFSE SC', credits: '5' },
    { accreditation: 'ICM Life Insurance', credits: '10' },
    { accreditation: 'ICBC Life Insurance', credits: '30' },
    { accreditation: 'AIC Life Insurance', credits: '20' },
  ];

  const courses = [
    {
      title: 'Investment Funds in Canada (IFC) Exam Prep',
      description: 'Complete preparation for the IFIC Investment Funds in Canada exam. Master mutual funds, segregated funds, ETFs, and investment strategies for Canadian markets.',
      features: [
        'All 18 chapters covered with video tutorials',
        'Practice questions & mock exams with explanations',
        'Study notes and quick reference guides',
        'Lifetime access to all course materials',
      ],
      link: 'https://www.udemy.com/course/investment-funds-in-canada-ifc-ific-tutorials/?couponCode=ST16MT230625G1',
      icon: '📈',
      students: '5,000+',
      hours: '10+',
    },
    {
      title: 'Canadian Securities Course (CSC) Exam Prep',
      description: 'Master the Canadian Securities Course with comprehensive tutorials covering equities, fixed income, derivatives, portfolio management, and all CSC exam topics.',
      features: [
        'Complete CSC curriculum coverage for both volumes',
        'Chapter-by-chapter breakdown with examples',
        'Practice questions and mock exams included',
        'Expert instructor guidance from CFA charterholder',
      ],
      link: 'https://www.udemy.com/course/prep-for-canadian-securities-course-csc/?referralCode=8BC660A43F94B385311A&couponCode=ST16MT230625G1',
      icon: '📊',
      students: '8,000+',
      hours: '15+',
    },
  ];

  const additionalCourses = [
    {
      title: 'Investment Funds in Canada (IFC/IFIC) Question Bank',
      description: 'Extensive practice questions to prepare for your IFC exam. Test your knowledge across all chapters.',
      link: 'https://www.udemy.com/course/investment-funds-in-canada-ifc-ific-question-bank/?couponCode=FEB-DEAL',
      icon: '📝',
      type: 'IFC',
    },
    {
      title: 'Investment Funds in Canada (IFIC) - 6 Full-length Mock Exams',
      description: 'Six complete mock exams simulating the real IFC exam. Perfect for final preparation.',
      link: 'https://www.udemy.com/course/investment-funds-in-canada-ific-6-full-length-mock-exams/?couponCode=FEB-DEAL',
      icon: '📋',
      type: 'IFC',
    },
    {
      title: 'Canadian Securities Course (CSC) Level 1 Question Bank',
      description: 'Comprehensive question bank for CSC Level 1. Practice with hundreds of exam-style questions.',
      link: 'https://www.udemy.com/course/canadian-securities-course-csc-level-1-question-bank-2023/?couponCode=FEB-DEAL',
      icon: '📝',
      type: 'CSC',
    },
    {
      title: 'Canadian Securities Course (CSC) Level 2 Question Bank',
      description: 'Complete question bank for CSC Level 2. Master advanced topics with practice questions.',
      link: 'https://www.udemy.com/course/canadian-securities-course-csc-level-2-question-bank-2023/?couponCode=FEB-DEAL',
      icon: '📝',
      type: 'CSC',
    },
  ];

  const benefits = [
    { icon: GraduationCap, title: 'Expert CSC & IFC Instruction', description: 'Learn from a Harvard & MIT certified instructor with CFA designation and real industry experience' },
    { icon: Clock, title: 'Self-Paced Learning', description: 'Lifetime access to all course materials. Study for your CSC or IFC exam whenever suits you' },
    { icon: Users, title: 'Join 13,000+ Students', description: 'Part of a community of successful candidates who passed their Canadian securities exams' },
    { icon: Award, title: 'High Pass Rates', description: 'Proven exam preparation approach with comprehensive coverage of CSC and IFC exam topics' },
  ];

  // Enhanced structured data for IFC course
  const courseSchema = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "itemListElement": [
      {
        "@type": "Course",
        "position": 1,
        "name": "Investment Funds in Canada (IFC) Exam Prep Course",
        "description": "Complete IFC exam preparation covering mutual funds, segregated funds, ETFs, Know Your Client rules, and investment fund regulations in Canada. Prepares for Mutual Fund Dealer – Dealing Representative License.",
        "provider": {
          "@type": "Person",
          "name": "Harry Vadalkar"
        },
        "educationalLevel": "Professional",
        "timeRequired": "PT90H",
        "numberOfCredits": "15",
        "about": ["IFC Exam", "Investment Funds Canada", "Mutual Funds", "IFIC Certification", "Mutual Fund Dealer License", "Know Your Client"],
        "teaches": ["Mutual fund investing", "Segregated funds", "ETF analysis", "Investment fund regulations", "Client portfolio management", "Behavioural finance"]
      },
      {
        "@type": "Course",
        "position": 2,
        "name": "Canadian Securities Course (CSC) Exam Prep",
        "description": "Comprehensive CSC exam preparation covering equities, fixed income, derivatives, and portfolio management for Canadian securities licensing",
        "provider": {
          "@type": "Person",
          "name": "Harry Vadalkar"
        },
        "educationalLevel": "Professional",
        "about": ["CSC Exam", "Canadian Securities Course", "Securities Licensing Canada"],
        "teaches": ["Equity analysis", "Fixed income securities", "Derivatives", "Portfolio management", "Canadian securities regulations"]
      }
    ]
  };

  // FAQ Schema for IFC
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "What is the IFC exam passing grade?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "The IFC exam passing grade is 60%. The exam consists of 100 multiple choice questions and you have 3 hours to complete it."
        }
      },
      {
        "@type": "Question",
        "name": "How many hours of study are required for the IFC exam?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "The IFC course requires 90-140 hours of study. You have 1 year to complete the course and exam."
        }
      },
      {
        "@type": "Question",
        "name": "What license does the IFC course prepare you for?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Successful completion of the IFC course helps you meet regulatory requirements to register for the Mutual Fund Dealer – Dealing Representative License."
        }
      }
    ]
  };

  return (
    <div className="min-h-screen" data-testid="ifc-csc-page">
      <Helmet>
        <title>IFC Exam Prep 2026 | Investment Funds in Canada Course | CSC Exam | Mutual Fund License | IFIC Certification | Harry Vadalkar</title>
        <meta name="description" content="Pass your IFC exam and CSC exam with comprehensive preparation courses. Investment Funds in Canada (IFC) course covers all 18 chapters including mutual funds, segregated funds, ETFs, Know Your Client rules, behavioural finance, and portfolio management. Prepare for your Mutual Fund Dealer License with 13,000+ successful students. CSC exam prep for Level 1 and Level 2 with question banks and tutorials." />
        <meta name="keywords" content="IFC exam prep, IFC exam preparation 2026, IFC course, IFC tutorials, Investment Funds in Canada, IFC exam, IFIC exam, IFC certification, IFC exam pass rate, IFC exam difficulty, IFC exam tips, mutual fund license Canada, mutual fund dealer license, mutual fund representative license, mutual fund dealer dealing representative, Know Your Client rule, KYC Canada, KYC compliance, IFC exam questions, IFC question bank, IFC mock exams, IFC exam weighting, IFC passing grade, IFC pass mark, IFC study guide, IFC chapters, IFC chapter breakdown, mutual funds course Canada, IFC exam structure, IFC exam format, IFC exam duration, IFC continuing education credits, CSC exam prep, CSC course, CSC tutorials, Canadian Securities Course, CSC exam 2026, CSC level 1, CSC level 2, CSC question bank, CSC mock exam, securities licensing Canada, IFC vs CSC comparison, mutual fund sales representative, MFDA exam, segregated funds IFC, ETF IFC exam, behavioural finance IFC, client focused reforms" />
        <link rel="canonical" href="https://harryvadalkar.ca/ifc-csc" />
        <meta property="og:title" content="IFC Exam Prep & CSC Course | Investment Funds in Canada | Mutual Fund License" />
        <meta property="og:description" content="Pass your Investment Funds in Canada (IFC) and Canadian Securities Course (CSC) exams. Complete preparation for Mutual Fund Dealer License. 13,000+ successful students." />
        <script type="application/ld+json">
          {JSON.stringify(courseSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
      </Helmet>

      {/* Hero Section */}
      <section className="relative py-20 md:py-32 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1758270704262-ecc82b23dc37?w=1920&q=80"
            alt="IFC and CSC Exam Preparation Classroom"
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#020617]/80 to-[#020617]" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 bg-blue-500/20 text-blue-400 px-4 py-2 rounded-full mb-6">
            <GraduationCap className="w-4 h-4" />
            <span className="font-medium">IFIC & CSI Exam Preparation</span>
          </div>
          <h1 className="font-heading text-5xl md:text-6xl font-bold text-white leading-tight mb-6">
            IFC & CSC <span className="text-blue-400">Exam Prep</span>
          </h1>
          <p className="text-xl text-slate-300 max-w-3xl mx-auto mb-8">
            Comprehensive preparation for <strong>Investment Funds in Canada (IFC)</strong> and <strong>Canadian Securities Course (CSC)</strong> exams. Get your <strong>Mutual Fund Dealer License</strong> with confidence.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a
              href="#ifc-details"
              className="inline-flex items-center bg-blue-600 hover:bg-blue-700 text-white font-semibold px-6 py-3 rounded-full transition-all"
            >
              <BookOpen className="w-5 h-5 mr-2" />
              IFC Course Details
            </a>
            <a
              href="https://www.udemy.com/course/investment-funds-in-canada-ifc-ific-tutorials/?couponCode=ST16MT230625G1"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center bg-amber-500 hover:bg-amber-600 text-black font-semibold px-6 py-3 rounded-full transition-all"
            >
              <ExternalLink className="w-5 h-5 mr-2" />
              Enroll Now
            </a>
          </div>
        </div>
      </section>

      {/* IFC Course Details Section */}
      <section id="ifc-details" className="py-20 bg-slate-900/30" data-testid="ifc-details">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-heading text-4xl md:text-5xl font-bold text-white mb-4">
              Investment Funds in Canada <span className="text-blue-400">(IFC)</span>
            </h2>
            <p className="text-slate-400 text-lg max-w-3xl mx-auto">
              The IFC course provides foundational knowledge that prepares advisors to give clients effective advice on mutual fund investments based on their objectives, timeline and risk tolerance.
            </p>
          </div>

          {/* IFC Overview Cards */}
          <div className="grid md:grid-cols-3 gap-6 mb-16">
            <div className="glass rounded-xl p-6">
              <Target className="w-10 h-10 text-blue-400 mb-4" />
              <h3 className="font-semibold text-xl text-white mb-3">Career Outcome</h3>
              <p className="text-slate-400">
                Successful completion helps you meet regulatory requirements to register for the <strong className="text-blue-400">Mutual Fund Dealer – Dealing Representative License</strong>.
              </p>
            </div>
            <div className="glass rounded-xl p-6">
              <Clock className="w-10 h-10 text-amber-400 mb-4" />
              <h3 className="font-semibold text-xl text-white mb-3">Study Requirements</h3>
              <p className="text-slate-400">
                <strong className="text-amber-400">90-140 hours</strong> of study with a <strong className="text-amber-400">1 year</strong> enrolment period to complete the course and exam.
              </p>
            </div>
            <div className="glass rounded-xl p-6">
              <Award className="w-10 h-10 text-green-400 mb-4" />
              <h3 className="font-semibold text-xl text-white mb-3">CE Credits</h3>
              <p className="text-slate-400">
                Earn <strong className="text-green-400">15 IFSE SFPA credits</strong>, plus credits from ICM, ICBC, and AIC for Life Insurance.
              </p>
            </div>
          </div>

          {/* Skills You'll Develop */}
          <div className="glass rounded-2xl p-8 mb-12">
            <h3 className="font-heading text-2xl font-bold text-white mb-6">Skills You'll Develop in the IFC Course</h3>
            <div className="grid md:grid-cols-2 gap-4">
              {ifcSkills.map((skill, index) => (
                <div key={index} className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                  <span className="text-slate-300">{skill}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Who Should Enroll */}
          <div className="glass rounded-2xl p-8 mb-12">
            <h3 className="font-heading text-2xl font-bold text-white mb-6">Who Should Enroll in the IFC Course?</h3>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="w-16 h-16 rounded-full bg-blue-500/20 flex items-center justify-center mx-auto mb-4">
                  <FileText className="w-8 h-8 text-blue-400" />
                </div>
                <h4 className="font-semibold text-white mb-2">License Seekers</h4>
                <p className="text-slate-400 text-sm">Those seeking their license to sell mutual funds in Canada</p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 rounded-full bg-amber-500/20 flex items-center justify-center mx-auto mb-4">
                  <Users className="w-8 h-8 text-amber-400" />
                </div>
                <h4 className="font-semibold text-white mb-2">Financial Professionals</h4>
                <p className="text-slate-400 text-sm">Financial services professionals seeking to grow their knowledge</p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-4">
                  <GraduationCap className="w-8 h-8 text-green-400" />
                </div>
                <h4 className="font-semibold text-white mb-2">Knowledge Seekers</h4>
                <p className="text-slate-400 text-sm">Anyone looking to gain broader understanding of capital markets</p>
              </div>
            </div>
          </div>

          {/* IFC Course Syllabus */}
          <div className="glass rounded-2xl p-8 mb-12">
            <button
              onClick={() => setShowIfcSyllabus(!showIfcSyllabus)}
              className="w-full flex items-center justify-between text-left"
            >
              <h3 className="font-heading text-2xl font-bold text-white">IFC Course Syllabus (18 Chapters)</h3>
              {showIfcSyllabus ? (
                <ChevronUp className="w-6 h-6 text-blue-400" />
              ) : (
                <ChevronDown className="w-6 h-6 text-blue-400" />
              )}
            </button>
            {showIfcSyllabus && (
              <div className="mt-6 grid md:grid-cols-2 gap-3">
                {ifcChapters.map((chapter, index) => (
                  <div key={index} className="flex items-center gap-3 p-3 rounded-lg bg-slate-800/50">
                    <span className="text-blue-400 font-mono text-sm">{String(index + 1).padStart(2, '0')}</span>
                    <span className="text-slate-300 text-sm">{chapter.replace(`Chapter ${index + 1} - `, '')}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* IFC Exam Details Grid */}
          <div className="grid lg:grid-cols-2 gap-8">
            {/* Exam Weighting */}
            <div className="glass rounded-2xl p-8">
              <h3 className="font-heading text-2xl font-bold text-white mb-6 flex items-center gap-2">
                <Percent className="w-6 h-6 text-blue-400" />
                IFC Exam Weighting
              </h3>
              <div className="space-y-3">
                {ifcExamWeighting.map((item, index) => (
                  <div key={index} className="flex items-center justify-between p-3 rounded-lg bg-slate-800/50">
                    <span className="text-slate-300 text-sm">{item.topic}</span>
                    <span className="text-blue-400 font-bold">{item.weight}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Exam Structure */}
            <div className="glass rounded-2xl p-8">
              <h3 className="font-heading text-2xl font-bold text-white mb-6 flex items-center gap-2">
                <FileText className="w-6 h-6 text-amber-400" />
                IFC Exam Structure
              </h3>
              <div className="space-y-3">
                {ifcExamStructure.map((item, index) => (
                  <div key={index} className="flex items-center justify-between p-3 rounded-lg bg-slate-800/50">
                    <span className="text-slate-400 text-sm">{item.label}</span>
                    <span className="text-white font-semibold">{item.value}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* CE Credits Table */}
          <div className="glass rounded-2xl p-8 mt-8">
            <h3 className="font-heading text-2xl font-bold text-white mb-6 flex items-center gap-2">
              <Award className="w-6 h-6 text-green-400" />
              Continuing Education (CE) Credits
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              {ceCredits.map((item, index) => (
                <div key={index} className="text-center p-4 rounded-lg bg-slate-800/50">
                  <p className="text-3xl font-bold text-green-400 mb-1">{item.credits}</p>
                  <p className="text-slate-400 text-xs">{item.accreditation}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Course Cards Section */}
      <section className="py-20" data-testid="courses-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-heading text-4xl font-bold text-white mb-4">
              Enroll in Our <span className="text-blue-400">Exam Prep Courses</span>
            </h2>
            <p className="text-slate-400 text-lg">Join 13,000+ successful students who passed their exams</p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8">
            {courses.map((course, index) => (
              <div
                key={index}
                className="glass rounded-2xl overflow-hidden hover:border-blue-500/50 transition-all duration-300"
              >
                <div className="p-8">
                  <span className="text-5xl mb-4 block">{course.icon}</span>
                  <h2 className="font-heading text-2xl font-bold text-white mb-4">
                    {course.title}
                  </h2>
                  <p className="text-slate-400 mb-6">{course.description}</p>

                  <div className="flex gap-6 mb-6">
                    <div className="flex items-center gap-2">
                      <Users className="w-5 h-5 text-blue-400" />
                      <span className="text-slate-300">{course.students} students</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Clock className="w-5 h-5 text-blue-400" />
                      <span className="text-slate-300">{course.hours} hours</span>
                    </div>
                  </div>

                  <ul className="space-y-3 mb-8">
                    {course.features.map((feature, idx) => (
                      <li key={idx} className="flex items-center gap-3">
                        <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
                        <span className="text-slate-300">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  <a
                    href={course.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`enroll-${index === 0 ? 'ifc' : 'csc'}`}
                    className="inline-flex items-center w-full justify-center bg-blue-600 hover:bg-blue-700 text-white font-semibold px-6 py-4 rounded-xl transition-all"
                  >
                    <ExternalLink className="w-5 h-5 mr-2" />
                    Enroll on Udemy
                  </a>
                </div>
              </div>
            ))}
          </div>

          {/* Additional Courses - Question Banks & Mock Exams */}
          <div className="mt-16">
            <h3 className="font-heading text-2xl font-bold text-white mb-8 text-center">
              Question Banks & Mock Exams
            </h3>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {additionalCourses.map((course, index) => (
                <div
                  key={index}
                  className="glass rounded-xl p-6 hover:border-blue-500/50 transition-all duration-300 flex flex-col"
                >
                  <div className="flex items-center gap-2 mb-3">
                    <span className="text-2xl">{course.icon}</span>
                    <span className={`text-xs font-semibold px-2 py-1 rounded-full ${
                      course.type === 'IFC' ? 'bg-amber-500/20 text-amber-400' : 'bg-blue-500/20 text-blue-400'
                    }`}>
                      {course.type}
                    </span>
                  </div>
                  <h4 className="font-semibold text-white mb-2 text-sm line-clamp-2">{course.title}</h4>
                  <p className="text-slate-400 text-xs mb-4 flex-grow">{course.description}</p>
                  <a
                    href={course.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`additional-course-${index}`}
                    className="inline-flex items-center justify-center bg-slate-700 hover:bg-slate-600 text-white font-medium px-4 py-2 rounded-lg transition-all text-sm"
                  >
                    <ExternalLink className="w-4 h-4 mr-1" />
                    Enroll
                  </a>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-slate-900/30" data-testid="benefits-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-heading text-4xl font-bold text-white mb-4">
              Why Choose <span className="text-blue-400">Our Courses</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((benefit, index) => (
              <div
                key={index}
                className="glass rounded-xl p-6 text-center hover:border-blue-500/30 transition-all"
              >
                <benefit.icon className="w-10 h-10 text-blue-400 mx-auto mb-4" />
                <h3 className="font-semibold text-white mb-2">{benefit.title}</h3>
                <p className="text-slate-400 text-sm">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Browse All Courses CTA */}
      <section className="py-20" data-testid="browse-cta">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="glass rounded-2xl p-8 md:p-12 text-center">
            <div className="inline-flex items-center gap-2 bg-blue-500/20 text-blue-400 px-4 py-2 rounded-full mb-6">
              <GraduationCap className="w-4 h-4" />
              <span className="font-medium">18+ Courses Available</span>
            </div>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-4">
              Browse All <span className="text-blue-400">IFC & CSC Courses</span>
            </h2>
            <p className="text-slate-300 text-lg mb-8">
              Explore our complete collection of IFC tutorials, question banks, mock exams, and CSC preparation courses on Udemy.
            </p>
            <a
              href="/courses"
              data-testid="browse-all-courses"
              className="inline-flex items-center bg-blue-600 hover:bg-blue-700 text-white font-bold px-10 py-4 rounded-full transition-all shadow-lg hover:shadow-blue-500/30 text-lg"
            >
              <ExternalLink className="w-5 h-5 mr-2" />
              View All Courses
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default IfcCsc;
